import { createContext, useCallback, useContext, useState } from 'react';
import Modal from './Modal';
import Button from './Button';

/**
 * Accessible replacement for window.confirm(). Wrapped once at the root
 * (src/index.js). Usage:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, body, danger: true, confirmText: 'Delete' }))) return;
 *
 * `confirm` accepts an options object (or a plain string body) and resolves to
 * true/false. The dialog focuses Cancel first (safe default for destructive
 * actions), traps focus, and closes/cancels on Escape or overlay click.
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { opts, resolve } | null

  const confirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        const normalized = typeof opts === 'string' ? { body: opts } : opts || {};
        setState({ opts: normalized, resolve });
      }),
    []
  );

  const settle = useCallback((result) => {
    setState((s) => {
      if (s) s.resolve(result);
      return null;
    });
  }, []);

  const opts = state?.opts || {};
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal open={!!state} onClose={() => settle(false)} title={opts.title || 'Are you sure?'}>
        {opts.body && <p className="ui-modal__body">{opts.body}</p>}
        <div className="ui-modal__actions">
          <Button variant="secondary" onClick={() => settle(false)}>
            {opts.cancelText || 'Cancel'}
          </Button>
          <Button variant={opts.danger ? 'danger' : 'primary'} onClick={() => settle(true)}>
            {opts.confirmText || 'Confirm'}
          </Button>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}
